import { useCallback, useMemo, useState } from "react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { nowUtc } from "@/utils/appNow"

dayjs.extend(utc)

// Min/max waypoint timestamp (epoch ms) across routes. Scans every feature
// (robust against unsorted waypoints). Returns nulls when none are valid.
function routeTimestampRange(routes) {
  let minMs = null
  let maxMs = null
  for (const route of routes ?? []) {
    const features = route?.points?.geojson?.features ?? []
    for (const feature of features) {
      const ts = feature?.properties?.timestamp
      if (!ts) continue
      const ms = dayjs.utc(ts).valueOf()
      if (!Number.isFinite(ms)) continue
      if (minMs == null || ms < minMs) minMs = ms
      if (maxMs == null || ms > maxMs) maxMs = ms
    }
  }
  return { minMs, maxMs }
}

// Left edge of the time window: earliest waypoint across the displayed plan
// routes. The 7d history fallback applies only with history context (a selected
// vessel or the Reported toggle) — a weather-only slider starts at the current
// 6h tick instead of showing empty history. Also drives the reported-waypoints
// fetch window. The voyage-planner candidate route is a future route (departure
// onward) — it only extends the right edge, never the left, so it must not
// widen the reported/history window. Derived from epoch-ms so the dayjs
// identity is stable. Exposes a reported-refetch signal.
export function useWindowStart({ planRoutes, showHistory }) {
  const fromMs = useMemo(() => {
    let minMs = routeTimestampRange(planRoutes ?? []).minMs
    // A recalculated route starts at the ship's position; core stamps the
    // original departure so the reported history still reaches back to it.
    for (const route of planRoutes ?? []) {
      const departure = route?.summary?.departure_original_utc
      if (!departure) continue
      const ms = dayjs.utc(departure).valueOf()
      if (Number.isFinite(ms) && (minMs == null || ms < minMs)) minMs = ms
    }
    return minMs
  }, [planRoutes])

  // Round to UTC day (or the current 6h boundary) so the 6h tick grid hits
  // midnight ticks (slider only labels ticks at 00:00 UTC).
  const fromPicker = useMemo(() => {
    if (fromMs != null) return dayjs.utc(fromMs).startOf("day")
    if (showHistory) return nowUtc().subtract(7, "day").startOf("day")
    const now = nowUtc()
    return now.startOf("day").add(Math.floor(now.hour() / 6) * 6, "hour")
  }, [fromMs, showHistory])

  const [reportedRefreshTick, setReportedRefreshTick] = useState(0)
  const triggerReportedRefresh = useCallback(
    () => setReportedRefreshTick((t) => t + 1),
    [],
  )

  return { fromPicker, reportedRefreshTick, triggerReportedRefresh }
}

// Right edge of the time slider: the displayed plan/voyage route's last
// waypoint (past or future alike), extended by the weather range only while a
// weather layer is active. The reported track is clipped to this window and
// only sets the edge itself when no plan route is shown at all. Fallback:
// current hour. Derived from epoch-ms so the dayjs identity is stable.
export function useSliderRightEdge({
  weatherMaxTimestamp,
  planRoutes,
  reportedRoutes,
  draftRoutes,
  weatherActive,
}) {
  // Letzter selektierbarer Zeitpunkt der benchmap-Timeline — dieselbe Quelle, die die Wetterkarte rendert
  const weatherMaxMs = useMemo(() => {
    if (!weatherMaxTimestamp) return null
    const ms = dayjs.utc(weatherMaxTimestamp).valueOf()
    return Number.isFinite(ms) ? ms : null
  }, [weatherMaxTimestamp])

  const routeMaxMs = useMemo(() => {
    const routes = [...(planRoutes ?? []), ...(draftRoutes ?? [])]
    return routeTimestampRange(routes).maxMs
  }, [planRoutes, draftRoutes])

  const reportedMaxMs = useMemo(
    () => routeTimestampRange(reportedRoutes ?? []).maxMs,
    [reportedRoutes],
  )

  // Weather only extends the right edge while a weather layer is shown; a
  // past-only route otherwise ends the slider at its last waypoint.
  const effectiveWeatherMaxMs = weatherActive ? weatherMaxMs : null

  // A route that lies entirely in the past owns the right edge alone — weather
  // only extends routes that reach into the future (or the no-route fallback).
  const routeIsPast =
    routeMaxMs != null && routeMaxMs < nowUtc().valueOf()

  // The plan/voyage route (+ active weather) owns the right edge. Reported only
  // sets it when no plan route is shown, so a past-only archived route ends the
  // slider at its last waypoint instead of being stretched to the reported end.
  const planEdgeMs = routeIsPast
    ? routeMaxMs
    : Math.max(effectiveWeatherMaxMs ?? 0, routeMaxMs ?? 0)

  // Without a plan route the reported edge always counts — a weather scene with
  // a short timeline must not clip the reported track (the map clips at this
  // edge via clipRoutesToTimestamp).
  const toPickerMs =
    routeMaxMs != null
      ? planEdgeMs
      : Math.max(planEdgeMs, reportedMaxMs ?? 0) ||
        nowUtc().startOf("hour").valueOf()

  return useMemo(() => dayjs.utc(toPickerMs), [toPickerMs])
}
